/**
 * Regras de data e hora da viagem — porta `cteatf_datas.dart`.
 *
 * Antecedência mínima, reprojeção da ATF de referência, comparação ida×volta.
 * O relógio é sempre parâmetro (`agora`), nunca `new Date()` direto, para a
 * fronteira das 6h ser testável sem esperar o dia virar.
 */

export interface HoraDoDia { hour: number; minute: number }

/** Antecedência mínima para emitir no próprio dia (regra SGTF/DER). */
export const ANTECEDENCIA_MINIMA_HORAS = 6;

/** Janela comercial default, usada quando não há ATF de referência. */
export const HORA_IDA_DEFAULT: HoraDoDia = { hour: 8, minute: 0 };
export const HORA_VOLTA_DEFAULT: HoraDoDia = { hour: 18, minute: 0 };

export interface DatasViagem {
  ida: Date;
  horaIda: HoraDoDia;
  volta: Date;
  horaVolta: HoraDoDia;
  /** Referência (dd/MM/yyyy) de onde a sugestão veio; `null` = veio do default. */
  referencia: string | null;
}

function meiaNoite(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function comHora(d: Date, t: HoraDoDia): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), t.hour, t.minute);
}

export function mesmaData(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Menor dia em que `hora` ainda respeita a antecedência mínima: hoje ou amanhã. */
export function diaParaHorario(hora: HoraDoDia, agora: Date): Date {
  const hoje = meiaNoite(agora);
  const hojeComHora = comHora(agora, hora);
  const diffHoras = (hojeComHora.getTime() - agora.getTime()) / 3_600_000;
  if (diffHoras >= ANTECEDENCIA_MINIMA_HORAS) return hoje;
  const amanha = new Date(hoje);
  amanha.setDate(amanha.getDate() + 1);
  return amanha;
}

/** Ida e volta no mesmo dia, na janela comercial, já sujeitas à antecedência. */
export function datasDefault(agora: Date): DatasViagem {
  const ida = diaParaHorario(HORA_IDA_DEFAULT, agora);
  return { ida, horaIda: HORA_IDA_DEFAULT, volta: ida, horaVolta: HORA_VOLTA_DEFAULT, referencia: null };
}

/** `"07:30"` → HoraDoDia. `null` se inválida. */
export function parseHoraAtf(v: unknown): HoraDoDia | null {
  const partes = (typeof v === 'string' ? v : '').split(':');
  if (partes.length < 2) return null;
  const h = parseInt(partes[0], 10);
  const m = parseInt(partes[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { hour: h, minute: m };
}

interface AtfReferencia {
  data_ida?: string | null;
  horario_ida?: string | null;
  somente_ida?: boolean | null;
  data_volta?: string | null;
  horario_volta?: string | null;
}

function diffDias(a: Date, b: Date): number {
  return Math.round((meiaNoite(a).getTime() - meiaNoite(b).getTime()) / 86_400_000);
}

/**
 * Reprojeta os horários da ATF de referência no próximo dia válido —
 * preserva o VÃO da viagem original, não as datas em si. Cai em
 * `datasDefault` (nunca lança) quando a referência não serve.
 */
export function datasDaUltimaAtf(r: AtfReferencia, agora: Date, maxDiasAgendamento: number): DatasViagem {
  const dataIda = r.data_ida ? new Date(r.data_ida) : null;
  const horaIda = parseHoraAtf(r.horario_ida);
  if (!dataIda || isNaN(dataIda.getTime()) || !horaIda) return datasDefault(agora);

  let ida = diaParaHorario(horaIda, agora);

  const refDia = meiaNoite(dataIda);
  if (ida.getTime() === refDia.getTime()) {
    ida = new Date(ida);
    ida.setDate(ida.getDate() + 1);
  }

  if (diffDias(ida, agora) > maxDiasAgendamento) return datasDefault(agora);

  const referencia = `${String(dataIda.getDate()).padStart(2, '0')}/`
    + `${String(dataIda.getMonth() + 1).padStart(2, '0')}/${dataIda.getFullYear()}`;

  const somenteIdaRef = r.somente_ida === true;
  const dataVolta = r.data_volta ? new Date(r.data_volta) : null;
  const horaVoltaRef = parseHoraAtf(r.horario_volta);
  if (!somenteIdaRef && dataVolta && !isNaN(dataVolta.getTime()) && horaVoltaRef) {
    const delta = diffDias(dataVolta, dataIda);
    const volta = new Date(ida);
    volta.setDate(volta.getDate() + Math.max(delta, 0));
    if (comHora(volta, horaVoltaRef).getTime() > comHora(ida, horaIda).getTime()) {
      return { ida, horaIda, volta, horaVolta: horaVoltaRef, referencia };
    }
  }

  const minutosIda = horaIda.hour * 60 + horaIda.minute;
  const minutosVoltaDefault = HORA_VOLTA_DEFAULT.hour * 60 + HORA_VOLTA_DEFAULT.minute;
  if (minutosVoltaDefault > minutosIda) {
    return { ida, horaIda, volta: ida, horaVolta: HORA_VOLTA_DEFAULT, referencia };
  }
  const voltaDiaSeguinte = new Date(ida);
  voltaDiaSeguinte.setDate(voltaDiaSeguinte.getDate() + 1);
  return { ida, horaIda, volta: voltaDiaSeguinte, horaVolta: horaIda, referencia };
}

/** A validação do passo de datas. `null` = pode avançar. */
export function erroDeDatas({
  ida, horaIda, volta, horaVolta, somenteIda,
}: { ida: Date; horaIda: HoraDoDia; volta: Date; horaVolta: HoraDoDia; somenteIda: boolean }): string | null {
  if (somenteIda) return null;
  if (comHora(volta, horaVolta).getTime() > comHora(ida, horaIda).getTime()) return null;
  return mesmaData(volta, ida)
    ? 'O horário de volta deve ser depois do de ida'
    : 'A data de volta não pode ser anterior à de ida';
}
